/**
 * Project Store - Zustand State Management
 *
 * Manages project state including list, creation, update, and deletion.
 */

import { create } from 'zustand';
import {
  ProjectWithStats,
  ProjectCostDataPoint,
  CreateProjectRequest,
  UpdateProjectRequest,
} from '@budgira/shared';
import { projectService } from '../services/project.service';
import i18n from '../i18n';

interface ProjectState {
  projects: ProjectWithStats[];
  currentProject: ProjectWithStats | null;
  costEvolution: ProjectCostDataPoint[];
  isLoading: boolean;
  isLoadingCostEvolution: boolean;
  error: string | null;
  includeArchived: boolean;

  // Actions
  fetchProjects: (forceIncludeArchived?: boolean) => Promise<void>;
  fetchProject: (id: string) => Promise<void>;
  fetchCostEvolution: (id: string) => Promise<void>;
  createProject: (data: CreateProjectRequest) => Promise<ProjectWithStats | null>;
  updateProject: (id: string, data: UpdateProjectRequest) => Promise<ProjectWithStats | null>;
  deleteProject: (id: string) => Promise<boolean>;
  archiveProject: (id: string) => Promise<boolean>;
  completeProject: (id: string) => Promise<boolean>;
  reactivateProject: (id: string) => Promise<boolean>;
  setIncludeArchived: (include: boolean) => void;
  clearCurrentProject: () => void;
  clearError: () => void;
  resetStore: () => void;
}

/**
 * Map backend error codes to localized messages
 */
function localizeProjectError(err: unknown): string {
  let errorCode: string | undefined;
  let errorMessage: string | undefined;

  if (err && typeof err === 'object') {
    const axiosError = err as {
      response?: { data?: { error?: { code?: string; message?: string } } };
    };
    errorCode = axiosError.response?.data?.error?.code;
    errorMessage = axiosError.response?.data?.error?.message;
  }

  // Map error codes to translation keys
  const errorKeyMap: Record<string, string> = {
    PROJECT_NOT_FOUND: 'projects.errors.notFound',
    UNAUTHORIZED: 'projects.errors.unauthorized',
    NAME_ALREADY_EXISTS: 'projects.errors.nameExists',
    VALIDATION_ERROR: 'projects.errors.validation',
  };

  if (errorCode && errorKeyMap[errorCode]) {
    const key = errorKeyMap[errorCode]!;
    return String(i18n.t(key));
  }

  // If error is an Error instance with message
  if (err instanceof Error) {
    return err.message;
  }

  return errorMessage || String(i18n.t('projects.errors.generic'));
}

export const useProjectStore = create<ProjectState>((set, get) => ({
  projects: [],
  currentProject: null,
  costEvolution: [],
  isLoading: false,
  isLoadingCostEvolution: false,
  error: null,
  includeArchived: false,

  fetchProjects: async (forceIncludeArchived?: boolean): Promise<void> => {
    set({ isLoading: true, error: null });
    try {
      const includeArchived = forceIncludeArchived ?? get().includeArchived;
      const projects = await projectService.list(includeArchived);
      set({ projects, isLoading: false });
    } catch (err) {
      const message = localizeProjectError(err);
      set({ error: message, isLoading: false });
    }
  },

  fetchProject: async (id: string): Promise<void> => {
    set({ isLoading: true, error: null });
    try {
      const project = await projectService.getById(id);
      set({ currentProject: project, isLoading: false });
    } catch (err) {
      const message = localizeProjectError(err);
      set({ error: message, isLoading: false });
    }
  },

  fetchCostEvolution: async (id: string): Promise<void> => {
    set({ isLoadingCostEvolution: true, error: null });
    try {
      const costEvolution = await projectService.getCostEvolution(id);
      set({ costEvolution, isLoadingCostEvolution: false });
    } catch (err) {
      const message = localizeProjectError(err);
      set({ error: message, isLoadingCostEvolution: false });
    }
  },

  createProject: async (data: CreateProjectRequest): Promise<ProjectWithStats | null> => {
    set({ isLoading: true, error: null });
    try {
      const created = await projectService.create(data);
      // Refresh the list to get updated stats
      await get().fetchProjects();
      // Find the created project with stats
      const projectWithStats = get().projects.find((p) => p.id === created.id) || null;
      set({ isLoading: false });
      return projectWithStats;
    } catch (err) {
      const message = localizeProjectError(err);
      set({ error: message, isLoading: false });
      return null;
    }
  },

  updateProject: async (
    id: string,
    data: UpdateProjectRequest
  ): Promise<ProjectWithStats | null> => {
    set({ isLoading: true, error: null });
    try {
      await projectService.update(id, data);
      // Refresh the list and current project
      await get().fetchProjects();
      const updated = get().projects.find((p) => p.id === id) || null;
      if (get().currentProject?.id === id && updated) {
        set({ currentProject: updated });
      }
      set({ isLoading: false });
      return updated;
    } catch (err) {
      const message = localizeProjectError(err);
      set({ error: message, isLoading: false });
      return null;
    }
  },

  deleteProject: async (id: string): Promise<boolean> => {
    set({ isLoading: true, error: null });
    try {
      await projectService.delete(id);
      // Remove from local state
      set((state) => ({
        projects: state.projects.filter((p) => p.id !== id),
        currentProject: state.currentProject?.id === id ? null : state.currentProject,
        isLoading: false,
      }));
      return true;
    } catch (err) {
      const message = localizeProjectError(err);
      set({ error: message, isLoading: false });
      return false;
    }
  },

  archiveProject: async (id: string): Promise<boolean> => {
    set({ isLoading: true, error: null });
    try {
      await projectService.archive(id);
      await get().fetchProjects();
      // Update current project if it's the one archived
      if (get().currentProject?.id === id) {
        const updated = get().projects.find((p) => p.id === id);
        if (updated) {
          set({ currentProject: updated });
        }
      }
      set({ isLoading: false });
      return true;
    } catch (err) {
      const message = localizeProjectError(err);
      set({ error: message, isLoading: false });
      return false;
    }
  },

  completeProject: async (id: string): Promise<boolean> => {
    set({ isLoading: true, error: null });
    try {
      await projectService.complete(id);
      await get().fetchProjects();
      // Update current project if it's the one completed
      if (get().currentProject?.id === id) {
        const updated = get().projects.find((p) => p.id === id);
        if (updated) {
          set({ currentProject: updated });
        }
      }
      set({ isLoading: false });
      return true;
    } catch (err) {
      const message = localizeProjectError(err);
      set({ error: message, isLoading: false });
      return false;
    }
  },

  reactivateProject: async (id: string): Promise<boolean> => {
    set({ isLoading: true, error: null });
    try {
      await projectService.reactivate(id);
      await get().fetchProjects();
      // Update current project if it's the one reactivated
      if (get().currentProject?.id === id) {
        const updated = get().projects.find((p) => p.id === id);
        if (updated) {
          set({ currentProject: updated });
        }
      }
      set({ isLoading: false });
      return true;
    } catch (err) {
      const message = localizeProjectError(err);
      set({ error: message, isLoading: false });
      return false;
    }
  },

  setIncludeArchived: (include: boolean): void => {
    set({ includeArchived: include });
  },

  clearCurrentProject: (): void => {
    set({ currentProject: null, costEvolution: [] });
  },

  clearError: (): void => {
    set({ error: null });
  },

  resetStore: (): void => {
    set({
      projects: [],
      currentProject: null,
      costEvolution: [],
      isLoading: false,
      isLoadingCostEvolution: false,
      error: null,
    });
  },
}));

export default useProjectStore;
